import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  MenuItem,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import BillSummaryReportService from 'services/BillSummaryReportService';
import SalespersonsService from 'services/SalesPersonService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { SUBSCRIPTION_TYPE, SelectFilterStyle, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import TerminalService from 'services/TerminalServices';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function DayWiseReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(DateTimeHelpers.getFiveDateEarlier());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const [terminals, setTerminals] = useState([]);
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    date: tCommon('Date'),
    num_bills: tCommon('Sales'),
    tax: {
      title: tCommon('Tax Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.tax || 0);
      },
    },
    cess: {
      title: tCommon('Additional Tax'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.cess || 0);
      },
    },
    discount: {
      title: tCommon('Discount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.discount || 0);
      },
    },
    total: {
      title: tCommon('Total Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      filterTerminal !== prevFilterData.filterTerminal ||
      filterSalesperson !== prevFilterData.filterSalesperson
    );
  }, [
    filterFromDate,
    filterToDate,
    filterTerminal,
    filterSalesperson,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
  ]);

  useEffect(() => {
    // getMachineName();
    if (subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM) return;
    generateSummeryReport();
    getSalespersons();
    getTerminals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(tCommon('Day Wise Report'), filterFromDate, filterToDate, headerData, filteredItems, {
      [tCommon('Total Sales')]: summary.totalSales,
      [tCommon('Total Tax Amount')]: `${shop?.currency || ''} ${summary.totalTax}`,
      [tCommon('Total Additional Tax')]: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
      [tCommon('Total Discount')]: `${shop?.currency || ''} ${summary.totalDiscount}`,
      [tCommon('Total Amount')]: `${shop?.currency} ${summary.totalAmount}`,
    });
  };

  const generateSummeryReport = () => {
    const { startDate, endDate } = dynamicDateRange('DAY_WISE_REPORT');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadBillSummaryReports({
      startTime: startDate,
      endTime: endDate,
      cashier_ids: filterSalesperson,
      terminal_ids: filterTerminal,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      filterSalesperson,
      filterTerminal,
    });
  };

  const getTerminals = async () => {
    const res = await TerminalService.getTerminals();
    setTerminals(res);
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };

  const toCsvBtnPressed = async () => {
    const titleLine = tCommon('Day Wise Report for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredItems, CSV_COLUMNS, titleLine);

    // Create total row
    const summary = _getBillReportSummary(filteredItems);
    const fields = Object.keys(CSV_COLUMNS);
    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'date':
          return tCommon('Total');
        case 'num_bills':
          return summary.totalSales;
        case 'tax':
          return summary.totalTax;
        case 'cess':
          return summary.totalAdditionalTax;
        case 'discount':
          return summary.totalDiscount;
        case 'total':
          return summary.totalAmount;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `DAY-WISE-SALES-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const _getBillReportSummary = (items) => {
    let totalSales = 0;
    let totalExpence = 0;
    let totalAmount = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;
    let totalDiscount = 0;
    items.forEach((item) => {
      totalSales += item.num_bills;
      totalExpence += item.purchase_expense;
      totalAmount += item.total;
      totalTax += item.tax;
      totalAdditionalTax += item.cess;
      totalDiscount += item.discount;
    });

    return {
      totalSales: ShopsHelper.getAmountFormatted(shop, totalSales),
      totalExpence: ShopsHelper.getAmountFormatted(shop, totalExpence),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
      totalDiscount: ShopsHelper.getAmountFormatted(shop, totalDiscount),
    };
  };

  const loadBillSummaryReports = async ({
    startTime = filterFromDate,
    endTime = filterToDate,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
  } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }

    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('DAY_WISE_REPORT');

    const start = startTime || dynamicStartDate;
    const end = endTime || dynamicEndDate;

    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(start),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(end),
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
      };
      const resp = await BillSummaryReportService.getDayWiseReport(data);
      setItems(resp);
      setFilteredItems(resp);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredItems(items.filter((item) => item.date.includes(value.toLowerCase())));
  };

  const subscriptionType = shop?.subscription?.type || '';
  const headerData = [
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('Sales'),
      id: 'num_bills',
      type: 'text',
    },
    {
      label: tCommon('Tax Amount'),
      id: 'tax',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax'),
      id: 'cess',
      type: 'floatAmount',
    },
    {
      label: tCommon('Discount'),
      id: 'discount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.total)}`;
      },
    },
  ];

  const summary = _getBillReportSummary(filteredItems);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>

      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tCommon('Day Wise Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>

          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                className={styles.searchInput}
                placeholder={tCommon('Search by date')}
              />
              <Tooltip title={tCommon('Search')} placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>

        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="DAY_WISE_REPORT"
            />
          </div>

          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Terminal')}</label>
            </div>
            <FormControl>
              <Select
                multiple
                displayEmpty
                value={filterTerminal}
                onChange={(e) => setFilterTerminal(e.target.value)}
                input={<OutlinedInput />}
                style={{ width: 135, height: 40 }}
                renderValue={(selected) => {
                  if (!selected.length) return <span>{tCommon('All')}</span>;
                  return selected.map((id) => terminals.find((t) => t.id === id).name).join(', ');
                }}
                MenuProps={SelectFilterStyle}
              >
                {terminals.map((terminal) => (
                  <MenuItem key={terminal.id} value={terminal.id}>
                    <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                    <ListItemText primary={terminal.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Salesperson')}</label>
            </div>
            <FormControl>
              <Select
                multiple
                displayEmpty
                value={filterSalesperson}
                onChange={(e) => setFilterSalesperson(e.target.value)}
                input={<OutlinedInput />}
                style={{ width: 135, height: 40 }}
                renderValue={(selected) => {
                  if (!selected.length) return <span>{tCommon('All')}</span>;
                  return selected.map((id) => salesPersons.find((s) => s.id === id).name).join(', ');
                }}
              >
                {salesPersons.map((person) => (
                  <MenuItem key={person.id} value={person.id}>
                    <Checkbox checked={filterSalesperson.includes(person.id)} color="primary" />
                    <ListItemText primary={person.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              onClick={generateSummeryReport}
              disabled={isShowGenerateButton === false || loadingIndicator}
            >
              {loadingIndicator ? (
                <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
              ) : (
                <SyncIcon className={styles.actionBtnIcon} />
              )}
              {tCommon('Generate')}
            </Button>
          </div>
        </div>
      </div>

      {isShowGenerateButton === null ? (
        <Info title={tReport('Set filters and click generate')} content={tReport('Report filters info')} />
      ) : (
        items &&
        (filteredItems && filteredItems.length ? (
          <>
            <div className={styles.summaryContainer}>
              <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
                <span className={styles.cardTitle}>{tCommon('Total Amount')}</span>
                <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalAmount}`}</div>
              </div>

              <div className={`${styles.summaryCard} ${styles.cardGreen}`}>
                <span className={styles.cardTitle}>{tCommon('Sales')}</span>
                <div className={styles.cardValue}>{summary.totalSales}</div>
              </div>

              <div className={`${styles.summaryCard} ${styles.cardOrange}`}>
                <span className={styles.cardTitle}>{tCommon('Tax Amount')}</span>
                <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalTax}`}</div>
                <div className={styles.cardDetails}>
                  <span>
                    {tCommon('Additional Tax')}:{' '}
                    <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${
                      summary.totalAdditionalTax
                    }`}</strong>
                  </span>
                </div>
              </div>

              <div className={`${styles.summaryCard} ${styles.cardPurple}`}>
                <span className={styles.cardTitle}>{tCommon('Discount')}</span>
                <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalDiscount}`}</div>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <Grayout open={isShowGenerateButton} />
              <DataTable
                columns={headerData}
                rows={filteredItems || items}
                rowKey="id"
                summary={{
                  date: tCommon('Total'),
                  num_bills: summary.totalSales,
                  tax: `${shop?.currency || ''} ${summary.totalTax}`,
                  cess: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
                  discount: `${shop?.currency || ''} ${summary.totalDiscount}`,
                  total: `${shop?.currency || ''} ${summary.totalAmount}`,
                }}
                pagination={true}
              />
            </div>
          </>
        ) : (
          !loadingIndicator && (
            <Info
              severity="warning"
              title={tReport('Oh no, there are no sales matching to your filter')}
              content={tReport(
                'There are no sales matching to your filter. Please verify your inputs to generate report.'
              )}
            />
          )
        ))
      )}

      {dateLimitPopup && <DateLimitDialog handleClose={() => setDateLimitPopup(false)} />}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(DayWiseReport);
